import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { featuredPartnerOffer, offerPath, wildfireStoreOffer } from '../utils/offerNavigation';
import TempStart from './TempStart';
import DreamBoostBanner from '../components/DreamBoostBanner';
import ShopMap, { normalizeLocalStores } from '../components/ShopMap';
import EntryMultiplierBadge from '../components/EntryMultiplierBadge';
import { getEntryMultiplierValue } from '../utils/dreamBoost';
import '../styles/Shop.css';

const BANNER_KEY = 'hc_shop_earn_dismissed_at';
const BANNER_WINDOW_MS = 2 * 60 * 60 * 1000;
const LOCAL_PAGE = 5;

function listFrom(payload, keys) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  for (let i = 0; i < keys.length; i += 1) {
    if (Array.isArray(payload[keys[i]])) return payload[keys[i]];
  }
  return [];
}

function cardList(payload) {
  return listFrom(payload, ['results', 'cards']);
}

function logoOf(item) {
  return item?.logoUrl || item?.small_logo_url || item?.large_logo_url || item?.logo || '';
}

function normalizeOnline(payload) {
  const seen = {};
  const stores = [];
  listFrom(payload, ['click', 'results']).forEach((item) => {
    if (!item || item.is_active === false) return;
    const logo = logoOf(item);
    const id = item.id != null ? String(item.id) : item.name || item.merchantName;
    if (!id || seen[id] || !logo) return;
    seen[id] = true;
    stores.push({
      id,
      name: item.name || item.merchantName || 'Store',
      logo,
      offer: wildfireStoreOffer(item),
      entryMultiplier: getEntryMultiplierValue(item),
    });
  });
  return stores;
}

function isOnlineOffer(item) {
  const type = String(item?.offer_type || '').toLowerCase();
  return type === 'click' || type === 'click_sso' || type === 'online';
}

function normalizePartners(payload) {
  return listFrom(payload, ['results'])
    .filter(
      (item) =>
        item &&
        item.is_active !== false &&
        (item.is_preferred_partner === true || item.isPreferredPartner === true)
    )
    .map((item) => {
      const large = item.large_logo_url || item.largeLogoUrl || '';
      const small = item.small_logo_url || item.smallLogoUrl || large;
      if (!large && !small) return null;
      const online = isOnlineOffer(item);
      const summary = String(item.summary || '').trim().split('\n')[0].trim();
      return {
        id: String(item.id),
        name: item.name || '',
        large,
        small,
        subtitle: summary || (online ? 'Shop online and earn' : 'Shop in person and earn'),
        badge: online ? 'Online' : 'In-person',
        online,
        offer: featuredPartnerOffer(item),
        entryMultiplier: getEntryMultiplierValue(item),
        order: Number(item.preferred_order ?? item.preferredOrder ?? item.top_order) || 0,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.order - a.order);
}

function placeLine(store) {
  const place = [store.city, store.state].filter(Boolean).join(', ');
  const miles =
    store.distance == null
      ? ''
      : `${store.distance < 10 ? store.distance.toFixed(1) : Math.round(store.distance)} mi`;
  return [place, miles].filter(Boolean).join(' · ');
}

function schemeLabel(scheme) {
  const value = String(scheme || '').trim().toLowerCase();
  if (value === 'visa') return 'Visa';
  if (value === 'mastercard') return 'Mastercard';
  if (value === 'amex' || value === 'american express') return 'Amex';
  if (!value) return 'Card';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function bannerHidden() {
  const at = Number(window.localStorage.getItem(BANNER_KEY));
  return Number.isFinite(at) && at > 0 && Date.now() - at < BANNER_WINDOW_MS;
}

function locate() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
    );
  });
}

function ShopPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const openOffer = (offer) => navigate(offerPath(offer), { state: { offer } });
  const [bannerOn, setBannerOn] = useState(() => !bannerHidden());
  const [partners, setPartners] = useState([]);
  const [online, setOnline] = useState([]);
  const [localStores, setLocalStores] = useState([]);
  const [origin, setOrigin] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [card, setCard] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [showAllPartners, setShowAllPartners] = useState(false);
  const [localShown, setLocalShown] = useState(LOCAL_PAGE);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const position = await locate();
      const [featured, offers, olive, cards] = await Promise.all([
        apiService.getFeaturedOffers({ is_preferred_partner: true }).catch(() => null),
        apiService.getWildfireOffers({ page: 1, pageSize: 24 }).catch(() => null),
        apiService
          .getOliveOffers({
            latitude: position?.latitude,
            longitude: position?.longitude,
            page: 1,
            pageSize: 200,
            includeOnline: false,
          })
          .catch(() => null),
        apiService.getPaymentCards().catch(() => null),
      ]);
      if (cancelled) return;
      setOrigin(position);
      setPartners(normalizePartners(featured));
      setOnline(normalizeOnline(offers));
      setLocalStores(normalizeLocalStores(olive));
      setMapReady(true);
      const linked = cardList(cards).find((item) => item && (item.active !== false));
      setCard(linked || null);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectStoreName = location.state?.selectStoreName || '';

  useEffect(() => {
    if (!selectStoreName || !localStores.length) return;
    const wanted = selectStoreName.trim().toLowerCase();
    const match = localStores.find((store) => store.name.trim().toLowerCase() === wanted);
    if (!match) return;
    setSelectedId(match.id);
    document.getElementById('shop-map')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selectStoreName, localStores]);

  const bannerLogos = useMemo(() => online.slice(0, 3), [online]);
  const partnerCards = showAllPartners ? partners : partners.slice(0, 2);
  const onlineCards = online.slice(0, 8);
  const localRows = localStores.slice(0, localShown);

  const dismissBanner = () => {
    window.localStorage.setItem(BANNER_KEY, String(Date.now()));
    setBannerOn(false);
  };

  return (
    <div className="shop-page">
      <DreamBoostBanner />
      {bannerOn ? (
        <section className="shop-banner">
          <button type="button" className="shop-banner-close" aria-label="Dismiss" onClick={dismissBanner}>
            <svg viewBox="0 0 12 12" aria-hidden="true">
              <path d="M2 2l8 8M10 2 2 10" />
            </svg>
          </button>
          <div className="shop-banner-copy">
            <h2>Earn as you shop</h2>
            <p>Pay like you always do, you earn points and support your team!</p>
            <button
              type="button"
              className="shop-banner-cta"
              onClick={() => document.getElementById('shop-online')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >
              Explore stores
            </button>
          </div>
          {bannerLogos.length ? (
            <div className="shop-banner-logos" aria-hidden="true">
              {bannerLogos.map((store) => (
                <span key={store.id}>
                  <img src={store.logo} alt="" />
                </span>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {partners.length ? (
        <section className="shop-block" aria-label="Preferred Partners">
          <div className="shop-block-head">
            <h2>Preferred Partners</h2>
            {partners.length > 2 ? (
              <button type="button" onClick={() => setShowAllPartners((open) => !open)}>
                {showAllPartners ? 'Show less' : 'View all'}
              </button>
            ) : null}
          </div>
          <div className={`shop-partners${showAllPartners ? ' is-all' : ''}`}>
            {partnerCards.map((partner) => (
              <button
                key={partner.id}
                type="button"
                className="shop-partner"
                onClick={() => openOffer(partner.offer)}
              >
                {partner.large ? <img className="shop-partner-photo" src={partner.large} alt="" /> : null}
                <span className="shop-partner-badge">{partner.badge}</span>
                <EntryMultiplierBadge item={partner} variant="block" />
                <span className="shop-partner-meta">
                  {partner.small ? <img src={partner.small} alt="" /> : <span className="shop-partner-mark" />}
                  <span>
                    <strong>{partner.name}</strong>
                    <em>{partner.subtitle}</em>
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      <section className="shop-block" id="shop-online" aria-label="Shop online">
        <div className="shop-block-head">
          <h2>Shop online</h2>
          {online.length > 8 ? (
            <button type="button" onClick={() => navigate('/shop/online')}>
              View all
            </button>
          ) : null}
        </div>
        {onlineCards.length ? (
          <ul className="shop-online">
            {onlineCards.map((store) => (
              <li key={store.id}>
                <button type="button" onClick={() => openOffer(store.offer)}>
                  <span className="shop-online-tile">
                    <img src={store.logo} alt="" />
                    <EntryMultiplierBadge item={store} />
                  </span>
                  <span>{store.name}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="shop-empty">Stores will show up here when offers are available.</p>
        )}
      </section>

      <section className="shop-block" aria-label="Eat local">
        <div className="shop-block-head">
          <h2>Eat local</h2>
          <button
            type="button"
            onClick={() => document.getElementById('shop-map')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
          >
            View map
          </button>
        </div>
        <div className="shop-local">
          <div className="shop-local-list">
            {localRows.length ? (
              <ul>
                {localRows.map((store) => (
                  <li key={store.id}>
                    <button
                      type="button"
                      className={selectedId === store.id ? 'is-selected' : ''}
                      onClick={() => setSelectedId(store.id)}
                    >
                      <span className="shop-local-logo">
                        {store.logo ? <img src={store.logo} alt="" /> : null}
                      </span>
                      <span className="shop-local-copy">
                        <strong>
                          {store.name}
                          <EntryMultiplierBadge item={store} variant="block" compact />
                        </strong>
                        <em>{placeLine(store)}</em>
                      </span>
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M6 3.5 10.5 8 6 12.5" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="shop-empty">Enable location to discover nearby stores.</p>
            )}
            {localStores.length > localShown ? (
              <button type="button" className="shop-more" onClick={() => setLocalShown((count) => count + LOCAL_PAGE)}>
                Show more
              </button>
            ) : null}
          </div>
          <div className="shop-map-col">
          <div className="shop-map-wrap" id="shop-map">
            <ShopMap
              stores={localStores}
              origin={origin}
              ready={mapReady}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onOrigin={setOrigin}
              onRegionStores={(list) => {
                setLocalStores(list);
                setLocalShown(LOCAL_PAGE);
              }}
            />
            {card?.last4 ? (
              <div className="shop-card-callout">
                <span aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <rect x="3" y="6" width="18" height="12" rx="2" />
                    <path d="M3 10h18" />
                  </svg>
                </span>
                <span>
                  <strong>
                    Use {schemeLabel(card.scheme)} ···· {card.last4}
                  </strong>
                  <em>Pay with this linked card to earn points.</em>
                </span>
              </div>
            ) : null}
          </div>
          {/* The chip the embed shows under its map: in-person points need a linked card. */}
          <span className="home-card-badge">
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" />
              <path d="M1.5 6.5h13" />
            </svg>
            Linked card required
          </span>
          </div>
        </div>
      </section>
    </div>
  );
}

function ShopGate() {
  if (!apiService.isAuthenticated()) return <TempStart />;
  return <ShopPage />;
}

export default ShopGate;
